
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AuthError, AuthApiError } from "@supabase/supabase-js";

// Component for consistent label styling across the form
const InputLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="text-[#1C1C1C] font-semibold text-base mb-2">{children}</div>
);

const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // State management for form data and UI states
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"email" | "otp">("email");
  const [authMode, setAuthMode] = useState<"otp" | "password">("otp");
  const [canResendOtp, setCanResendOtp] = useState(true);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Email validation function to ensure proper format
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Start countdown timer for resend button
  const startResendCountdown = () => {
    setCanResendOtp(false);
    setResendCountdown(60);
    
    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResendOtp(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handle sending OTP for login
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    console.log("Attempting to send OTP to:", email);

    // Validate email before making the request
    if (!isValidEmail(email)) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: "Please enter a valid email address",
      });
      setIsLoading(false);
      return;
    }
    
    try {
      // Send login OTP with proper configuration
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false, // Don't create user for login
          data: {
            username: email.split('@')[0],
          },
        },
      });

      if (error) {
        console.error("OTP error:", error);
        
        // Handle rate limiting specifically
        if (error.message.includes("security purposes") || error.message.includes("rate limit")) {
          toast({
            variant: "destructive",
            title: "Too Many Requests",
            description: "Please wait a moment before requesting another code.",
          });
          startResendCountdown();
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Check your email!",
          description: "We sent you a 6-digit verification code. Please check your email and enter it below.",
        });
        
        setStep("otp");
        startResendCountdown();
      }
      
    } catch (error: any) {
      console.error("OTP error:", error);
      let errorMessage = "An error occurred while sending the verification code";
      
      if (error.message) {
        errorMessage = error.message;
      }

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OTP verification for login
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    console.log("Attempting to verify OTP:", otp);

    // Validate OTP length
    if (otp.length !== 6) {
      toast({
        variant: "destructive",
        title: "Invalid Code",
        description: "Please enter the complete 6-digit verification code",
      });
      setIsLoading(false);
      return;
    }

    try {
      // Verify OTP with proper type for login
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'email' // Use consistent 'email' type
      });

      if (error) {
        console.error("OTP verification error:", error);
        
        // Handle specific error cases
        if (error.message.includes("expired") || error.message.includes("invalid")) {
          toast({
            variant: "destructive",
            title: "Code Expired",
            description: "Your verification code has expired. Please request a new one.",
          });
          // Reset to email step so user can request new code
          setStep("email");
          setOtp("");
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Welcome back!",
          description: "You have successfully signed in.",
        });
        
        navigate("/home");
      }
      
    } catch (error: any) {
      console.error("OTP verification error:", error);
      let errorMessage = "Verification failed. Please try again.";
      
      if (error.message) {
        errorMessage = error.message;
      }

      toast({
        variant: "destructive",
        title: "Verification Failed",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle resending OTP
  const handleResendOtp = async () => {
    if (!canResendOtp) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          data: {
            username: email.split('@')[0],
          },
        },
      });

      if (error) {
        if (error.message.includes("security purposes") || error.message.includes("rate limit")) {
          toast({
            variant: "destructive",
            title: "Please Wait",
            description: "You need to wait before requesting another code.",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "New code sent!",
          description: "Check your email for a new verification code.",
        });
        setOtp(""); // Clear previous OTP
        startResendCountdown();
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to resend code. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password-based sign in
  const handlePasswordSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    console.log("Attempting password sign in for:", email);

    // Validate email and password
    if (!isValidEmail(email)) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: "Please enter a valid email address",
      });
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      toast({
        variant: "destructive",
        title: "Invalid Password",
        description: "Password must be at least 6 characters long",
      });
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error("Password sign in error:", error);
        
        if (error.message.includes("Invalid login credentials")) {
          toast({
            variant: "destructive",
            title: "Invalid Credentials",
            description: "The email or password you entered is incorrect. Please try again.",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Welcome back!",
          description: "You have successfully signed in.",
        });
        
        navigate("/home");
      }
      
    } catch (error: any) {
      console.error("Password sign in error:", error);
      let errorMessage = "An error occurred while signing in";
      
      if (error.message) {
        errorMessage = error.message;
      }

      toast({
        variant: "destructive",
        title: "Sign In Failed",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-none shadow-none">
        <CardHeader className="text-center space-y-2 pb-6">
          <CardTitle className="text-[28px] font-bold tracking-tight">Welcome Back</CardTitle>
          <CardDescription className="text-[#6B7280] text-base">
            {authMode === "password" 
              ? "Enter your email and password to sign in"
              : step === "email" 
                ? "Enter your email to receive a verification code"
                : "Enter the 6-digit code sent to your email"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {authMode === "password" ? (
            <form onSubmit={handlePasswordSignIn} className="space-y-6">
              <div className="space-y-2">
                <InputLabel>Email</InputLabel>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 border-[#E5E7EB] text-base placeholder:text-[#9CA3AF]"
                />
              </div>
              <div className="space-y-2">
                <InputLabel>Password</InputLabel>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 border-[#E5E7EB] text-base placeholder:text-[#9CA3AF]"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90"
                disabled={isLoading}
              >
                {isLoading ? "Signing in..." : "Sign In"}
              </Button>
              <div className="text-center text-base space-y-2">
                <div>
                  <Button 
                    variant="link" 
                    className="p-0 text-primary font-semibold" 
                    onClick={() => setAuthMode("otp")}
                    type="button"
                  >
                    Use verification code instead
                  </Button>
                </div>
                <div>
                  <span className="text-[#6B7280]">Don't have an account? </span>
                  <Button 
                    variant="link" 
                    className="p-0 text-primary font-semibold" 
                    onClick={() => navigate("/signup")}
                  >
                    Sign up
                  </Button>
                </div>
              </div>
            </form>
          ) : step === "email" ? (
            <form onSubmit={handleSignIn} className="space-y-6">
              <div className="space-y-2">
                <InputLabel>Email</InputLabel>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 border-[#E5E7EB] text-base placeholder:text-[#9CA3AF]"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90"
                disabled={isLoading}
              >
                {isLoading ? "Sending code..." : "Send Verification Code"}
              </Button>
              <div className="text-center text-base space-y-2">
                <div>
                  <Button 
                    variant="link" 
                    className="p-0 text-primary font-semibold" 
                    onClick={() => setAuthMode("password")}
                    type="button"
                  >
                    Use password instead
                  </Button>
                </div>
                <div>
                  <span className="text-[#6B7280]">Don't have an account? </span>
                  <Button 
                    variant="link" 
                    className="p-0 text-primary font-semibold" 
                    onClick={() => navigate("/signup")}
                  >
                    Sign up
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-6">
              <div className="space-y-2">
                <InputLabel>Verification Code</InputLabel>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={(value) => setOtp(value)}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="text-sm text-gray-500 text-center mt-2">
                  Code sent to {email}
                </p>
              </div>
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90"
                disabled={isLoading}
              >
                {isLoading ? "Verifying..." : "Verify Code"}
              </Button>
              <div className="text-center text-base space-y-2">
                <div>
                  <span className="text-[#6B7280]">Didn't receive the code? </span>
                  <Button 
                    variant="link" 
                    className="p-0 text-primary font-semibold" 
                    onClick={handleResendOtp}
                    disabled={!canResendOtp || isLoading}
                  >
                    {canResendOtp ? "Resend code" : `Resend in ${resendCountdown}s`}
                  </Button>
                </div>
                <div>
                  <Button 
                    variant="link" 
                    className="p-0 text-primary font-semibold" 
                    onClick={() => {
                      setStep("email");
                      setOtp("");
                      setAuthMode("otp");
                    }}
                  >
                    Change email address
                  </Button>
                </div>
                <div>
                  <Button 
                    variant="link" 
                    className="p-0 text-primary font-semibold" 
                    onClick={() => setAuthMode("password")}
                    type="button"
                  >
                    Use password instead
                  </Button>
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
